import {ReactNode} from 'react'
import {Trans} from 'react-i18next'
import {PnlTone} from '../utils/kpiGrid'
import {KpiInfoTooltip} from './KpiInfoTooltip'
import {IconWrap, Tile, TileLabel, TileSub, TileTop, TileValue} from './KpiTile.styled'

type KpiMessage = {i18nKey: string; defaults: string}

type KpiTileProps = {
  /** The metric's icon (a Material-UI icon element). */
  icon: ReactNode
  /** The metric's label message (short name shown in the header row). */
  label: KpiMessage
  /** Preformatted primary value, e.g. "$784,432", "20%" or the "–" placeholder. */
  value: string
  /** Secondary sub-line under the value. */
  sub: ReactNode
  /** One-sentence definition shown behind the info icon. */
  tooltip: KpiMessage
  /** Colours the value; defaults to neutral. Only the P/L tiles pass a signed tone. */
  tone?: PnlTone
  /** Stable test id so each tile can be asserted independently. */
  testId: string
}

/**
 * A single KPI tile: an icon+label header with an info tooltip, one primary value, and a sub-line
 * (PT-5711). Purely presentational — every value is preformatted by the caller, so the tile never
 * touches raw numbers or issues a request.
 *
 * @param icon - The metric's icon element.
 * @param label - The metric name message.
 * @param value - The preformatted primary value string.
 * @param sub - The secondary caption node.
 * @param tooltip - The one-sentence definition message.
 * @param tone - Optional value colour tone (defaults to neutral).
 * @param testId - Stable data-testid for the tile.
 */
export const KpiTile = ({icon, label, value, sub, tooltip, tone = 'neutral', testId}: KpiTileProps) => (
  <Tile data-testid={testId}>
    <TileTop>
      <IconWrap>{icon}</IconWrap>
      <TileLabel>
        <Trans {...label} />
      </TileLabel>
      <KpiInfoTooltip message={tooltip} ariaMessage={label} />
    </TileTop>
    <TileValue tone={tone}>{value}</TileValue>
    <TileSub>{sub}</TileSub>
  </Tile>
)
